import { useState } from 'react';
import { AlertCircle, Check, Copy, ExternalLink, Info } from 'lucide-react';
import { quizzesApi } from '../../lib/api';
import type { VideoDriver } from '../../types/quizzes';

export default function VideoEmbedPanel({ slug, published, driver = 'native' }: { slug: string; published: boolean; driver?: VideoDriver }) {
  const [copied, setCopied] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const code = quizzesApi.getVideoEmbedCode(slug);
  const previewUrl = quizzesApi.getVideoPreviewUrl(slug);
  return (
    <div className="space-y-5">
      {!published && (
        <div className="p-3 bg-warning-soft border border-line rounded-lg text-warning text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> The quiz must be published before the embed works on your page.
        </div>
      )}
      {driver === 'voomly' && (
        <div className="p-3 bg-accent-soft border border-line rounded-lg text-sm text-ink-2 flex items-start gap-2">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-accent-ink" />
          <span>This quiz plays a Voomly video. The Voomly player loads inside the quiz automatically — paste only the script below. Don't also add Voomly's own embed code to the page, or the video will appear twice.</span>
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-ink-2 mb-2">Preview link</label>
        <div className="flex items-center gap-2 flex-wrap">
          <code className="font-mono text-xs bg-surface-2 border border-line rounded-lg px-3 py-2 break-all flex-1 min-w-0">{previewUrl}</code>
          <button type="button" onClick={() => { navigator.clipboard.writeText(previewUrl); setCopiedLink(true); setTimeout(() => setCopiedLink(false), 2000); }}
            className="p-2 bg-surface-2 border border-line rounded-lg text-ink-3 hover:text-ink" aria-label="Copy preview link">
            {copiedLink ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
          </button>
          <a href={published ? previewUrl : undefined} target="_blank" rel="noopener noreferrer" aria-disabled={!published}
            className={`p-2 bg-surface-2 border border-line rounded-lg ${published ? 'text-ink-3 hover:text-ink' : 'text-ink-3 opacity-50 pointer-events-none'}`} aria-label="Open preview">
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
        <p className="text-xs text-ink-3 mt-1.5 m-0">Open it on your phone to try the quiz before embedding it. Add <code className="font-mono">?first_name=Ana&amp;email=you@example.com</code> to test personalization. Completed runs sync to ClickFunnels like real ones.</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-ink-2 mb-2">Embed script</label>
        <div className="relative">
          <pre className="bg-[var(--code-bg)] text-[var(--code-ink)] border border-line rounded-[11px] p-3.5 pr-12 font-mono text-[12px] leading-relaxed overflow-auto whitespace-pre-wrap break-all">{code}</pre>
          <button
            type="button"
            onClick={() => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
            className="absolute top-2 right-2 p-2 bg-surface-2 rounded text-ink-3 hover:text-ink"
            aria-label="Copy embed code"
          >
            {copied ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <div className="text-sm text-ink-2 space-y-2">
        <p className="m-0">Paste this into a Custom JS/HTML element on your ClickFunnels page. The quiz appears right where the script is placed (or add <code className="font-mono text-xs">data-target="#my-div"</code> to place it elsewhere).</p>
        <p className="m-0">
          <code className="font-mono text-xs">data-first-name</code> and <code className="font-mono text-xs">data-email</code> are ClickFunnels merge fields: known contacts get a personalized quiz
          (use <code className="font-mono text-xs">{'{{first_name|there}}'}</code> in a question title) and skip typing their email. On pages without a known contact they are ignored.
        </p>
        <p className="m-0 text-ink-3 text-xs">You can also pass <code className="font-mono">first_name</code>, <code className="font-mono">last_name</code>, <code className="font-mono">email</code> or <code className="font-mono">phone</code> as URL parameters.</p>
      </div>
    </div>
  );
}
